import { useContext } from "react";
import { Box, Typography, Paper, keyframes } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { LayoutContext } from "../../context/LayoutContext";

/* ================= Animations ================= */

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const progressGrow = keyframes`
  from {
    width: 0%;
  }
  to {
    width: var(--progress-width);
  }
`;

/* ================= Funnel Stage ================= */

const FunnelStage = ({
  stage,
  count,
  dropRate,
  progressColor,
  progressWidth,
  isLast,
  delay,
  isHighlighted,
  collapsed,
}) => {
  const theme = useTheme();

  const resolvedProgressColor =
    progressColor === "primary.main"
      ? theme.palette.primary.main
      : progressColor;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        minWidth: collapsed ? 100 : 120,
        animation: `${slideIn} 0.5s ease-out ${delay}s both`,
        transition: "all 0.3s ease",
      }}
    >
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Paper
          elevation={0}
          sx={{
            p: collapsed ? 1.5 : 2,
            py: 1.5,
            borderRadius: 2.5,
            border: isHighlighted ? "none" : "1px solid",
            borderColor: "grey.200",
            backgroundColor: isHighlighted ? "primary.main" : "#fff",
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: isHighlighted ? "#fff" : "text.primary",
            }}
          >
            {stage}
          </Typography>
        </Paper>

        <Box
          sx={{
            height: 4,
            backgroundColor: alpha(resolvedProgressColor, 0.2),
            borderRadius: 2,
            mt: 0.5,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              backgroundColor: resolvedProgressColor,
              borderRadius: 2,
              "--progress-width": progressWidth,
              animation: `${progressGrow} 1s ease-out ${delay + 0.3}s both`,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: 12,
            color: "text.secondary",
            mt: 0.5,
          }}
        >
          {count}
        </Typography>
      </Box>
      {!isLast && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: collapsed ? 0.5 : 1,
            minWidth: collapsed ? 45 : 55,
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 14, color: "grey.400" }} />
          <Typography
            sx={{
              fontSize: 10,
              color: "#EF4444",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {dropRate}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/* ================= Funnel Overview ================= */

/* ================= Funnel helpers ================= */

const FUNNEL_STAGES = [
  { stage: "Lead", key: "totalLeads", suffix: "Leads" },
  { stage: "Pre-Consultation", key: "preConsultation", suffix: "Leads" },
  { stage: "Post-Consultation", key: "postConsultation", suffix: "Leads" },
  { stage: "Booking", key: "booked", suffix: "Bookings" },
  { stage: "Payment", key: "payment", suffix: "Paid" },
];

const buildFunnelData = (apiStats) => {
  if (!apiStats) return null;

  const fs = apiStats.funnel_stages || {};
  const total = apiStats.totalLeads || 0;

  // Raw counts from dedicated backend queries
  const rawCounts = FUNNEL_STAGES.map(({ stage, key, suffix }) => ({
    stage,
    suffix,
    count: key === "totalLeads" ? total : (fs[key] || 0),
  }));

  // Cumulative from bottom: each stage = own count + all later stages
  const counts = [...rawCounts];
  let cumSum = 0;
  for (let i = counts.length - 1; i >= 1; i--) {
    cumSum += rawCounts[i].count;
    counts[i].count = cumSum;
  }
  counts[0].count = total;

  return counts.map((item, i) => {
    const prevCount = i > 0 ? counts[i - 1].count : item.count;
    const dropPct = prevCount > 0
      ? Math.round(((prevCount - item.count) / prevCount) * 100)
      : 0;
    const progressWidth = total > 0
      ? `${Math.round((item.count / total) * 100)}%`
      : "0%";

    return {
      stage: item.stage,
      count: `${item.count} ${item.suffix}`,
      dropRate: `${dropPct}% drop`,
      progressColor: "primary.main",
      progressWidth,
      isHighlighted: false,
    };
  });
};

const FunnelOverview = ({ apiStats }) => {
  const { collapsed } = useContext(LayoutContext);

  const funnelData = buildFunnelData(apiStats) || [
    { stage: "Lead", count: "0 Leads", dropRate: "0% drop", progressColor: "primary.main", progressWidth: "0%", isHighlighted: false },
    { stage: "Pre-Consultation", count: "0 Leads", dropRate: "0% drop", progressColor: "primary.main", progressWidth: "0%", isHighlighted: false },
    { stage: "Post-Consultation", count: "0 Leads", dropRate: "0% drop", progressColor: "primary.main", progressWidth: "0%", isHighlighted: false },
    { stage: "Booking", count: "0 Bookings", dropRate: "0% drop", progressColor: "primary.main", progressWidth: "0%", isHighlighted: false },
    { stage: "Payment", count: "0 Paid", dropRate: "0% drop", progressColor: "primary.main", progressWidth: "0%", isHighlighted: false },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        px: 3,
        backgroundColor: "#fff",
        mb: 3,
        animation: `${fadeIn} 0.6s ease-out`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 13,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Funnel Overview
        </Typography>

        <Typography sx={{ fontSize: 12, color: "#22C55E", fontWeight: 500 }}>
          Real-time status
        </Typography>
      </Box>

      {/* Funnel Stages */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: {
            xs: "wrap",
            md: collapsed ? "nowrap" : "wrap",
            lg: "nowrap",
          },
          gap: { xs: 1, lg: 0 },
          transition: "all 0.3s ease",
        }}
      >
        {funnelData.map((item, index) => (
          <FunnelStage
            key={item.stage}
            {...item}
            isLast={index === funnelData.length - 1}
            delay={index * 0.1}
            collapsed={collapsed}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default FunnelOverview;
